import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { renderToStaticMarkup } from 'react-dom/server';
import ContentContainer from '../../../commonComponents/pageLayout/ContentContainer';
import ConfigsContext from '../../../contextProviders/ConfigsContext';
import CSRFContext from '../../../contextProviders/CSRFContext';
import useAsyncExport from '../../../commonComponents/hooks/useAsyncExport';
import SubHeader from '../../../commonComponents/pageLayout/SubHeader';
import TopicBanner from '../../../commonComponents/pageLayout/header/TopicBanner';
import Title from '../../../commonComponents/Title';
import { compose } from '../../../services/utilityFunctions';
import translate from '../../../commonComponents/translate';
import Select from '../../../commonComponents/forms/inputs/Select';
import TeamProp from '../../../commonComponents/propTypes/teamProp';
import Loader from '../../../services/loader';
import ReportTabBtns from '../tab/ReportTabBtns';
import { REPORT_TAB_TYPE } from '../propTypes/reportTabProp';
import AsyncSelector from '../../../commonComponents/forms/inputs/AsyncSelector';
import DateRange from '../../../commonComponents/forms/inputs/DateRange';
import { formatDate } from '../../../services/dateUtilityFunctions';
import List from '../../../commonComponents/lists/ServerSideList';
import BasicPortlet from '../../../commonComponents/portlets/BasicPortlet';
import { subTableJourney, renderStepName } from './journeyReportHelper';
import CertificateZipList from '../../../commonComponents/certificates/CertificateZipList';
import SignedDocumentsList from '../../../commonComponents/signedDocuments/SignedDocumentsList';

function ReportJourney({ t, teams }) {
  const { tenant } = useContext(ConfigsContext);
  const _csrf = useContext(CSRFContext);
  const isDirectSelling = tenant?.settings?.directSelling === true;
  const showTeamPicker = !isDirectSelling;
  const defaultTeam = teams.length > 0 ? teams[0]._id : null;

  const tableId = 'reportJourneyList';
  const tableEl = useRef(null);
  const loader = useRef(new Loader('#journeysReportsLayout'));
  const [isLoading, setLoading] = useState(false);

  useEffect(() => {
    if (loader.current) {
      if (isLoading) {
        loader.current.show();
      } else {
        loader.current.hide();
      }
    }
  }, [isLoading]);

  const [showReport, setShowReport] = useState(false);
  const initialFilterState = {
    team: defaultTeam,
    language: null,
    journey: null,
    startedDate: null,
    endedDate: null,
  };
  const [filters, setFilters] = useState({
    team: defaultTeam,
    language: null,
    journey: null,
    startedDate: null,
    endedDate: null,
  });

  const journeyId = filters.journey;
  const { startExport: startJourneyExport, isExporting: isJourneyExporting } =
    useAsyncExport({
      t,
      csrf: _csrf,
      enqueueUrl: journeyId ? `/jobs/analytics/journey/${journeyId}/xlsx` : '',
      statusUrl: jobId =>
        journeyId
          ? `/jobs/analytics/journey/${journeyId}/job/${jobId}/status`
          : '',
      downloadUrl: jobId =>
        journeyId
          ? `/excel-jobs/analytics/journey/${journeyId}/job/${jobId}/download`
          : '',
      loaderContainerId: 'journeyReportExportLoader',
    });
  const handleJourneyExport = useCallback(() => {
    if (!journeyId) return;
    const payload = {};
    if (filters.team) payload.team = filters.team;
    startJourneyExport(payload);
  }, [filters.team, journeyId, startJourneyExport]);

  const [journeyTotal, setJourneyTotal] = useState({
    field1: 0,
    field2: 0,
    field3: 0,
  });

  const initialState = {
    textBlockOne: `${t('reports.journeys.total')}`,
    descriptionBlockOne: `${t('reports.journeys.total')}`,
    textBlockTwo: `${t('reports.journeys.totalUsers')}`,
    descriptionBlockTwo: `${t('reports.journeys.totalUsers_description')}`,
    textBlockThree: `${t('reports.journeys.currentActiveUsers')}`,
    descriptionBlockThree: `${t(
      'reports.journeys.currentActiveUsers_description',
    )}`,
  };
  const [
    {
      textBlockOne,
      descriptionBlockOne,
      textBlockTwo,
      descriptionBlockTwo,
      textBlockThree,
      descriptionBlockThree,
    },
    setState,
  ] = useState(initialState);
  const [selectedJourney, setSelectedJourney] = useState(null);

  // Show Performance Report
  const handleShowReport = () => {
    setShowReport(true);
  };

  // Handle filters change
  const handleFilterChange = field => value => {
    if (field === 'team') {
      setFilters({ ...initialFilterState, team: value });
      setShowReport(false);
    } else if (field === 'journey') {
      console.log('*value', value);
      setFilters(prevState => ({
        ...prevState,
        journey: value?.id || null,
      }));
      setSelectedJourney(value?.item || null);
    } else {
      setFilters(prevState => ({ ...prevState, [field]: value }));
    }
  };

  // handle filters change
  const handleDateChange = useCallback((startedDate, endedDate) => {
    setFilters(prevState => ({ ...prevState, startedDate, endedDate }));
  }, []);

  /** region API REQUESTS */
  const getJourneyTotalURL = (journeyId, userId) => {
    if (journeyId && !userId) {
      // Only journey
      return `/api/analytics/journey/journeys-total/journey/${journeyId}`;
    }
    return '/api/analytics/journey/journeys-total';
  };

  const getJourneyTotal = params =>
    new Promise((resolve, reject) => {
      axios
        .get(getJourneyTotalURL(filters.journey, filters.user), {
          params,
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Games',
          },
        })
        .then(({ data }) => {
          // Different resolve for different request
          if (!filters.journey && filters.user) {
            // Only user
            resolve({
              field1: data.data.totalJourneys,
              field2: data.data.finishedJourneys,
              field3: data.data.activeJourneys,
            });
          } else if (filters.journey && !filters.user) {
            // Only journey
            resolve({
              field1: data.data.totalUsers,
              field2: data.data.usersCompleted,
              field3: data.data.usersInProgress,
            });
          } else if (filters.journey && filters.user) {
            // Journey && User
            resolve({
              field1: data.data.totalSteps,
              field2: data.data.finishedSteps,
              field3: data.data.totalScore,
            });
          } else {
            resolve({
              field1: data.data.totalJourneys,
              field2: data.data.totalUsers,
              field3: data.data.currentActiveUsers,
              // field3: `${data.data.bounceRate.toFixed(2)}%`,
            });
          }
        })
        .catch(err => {
          console.log(err);
          reject(err);
        });
    });

  const fetchInfo = params => {
    const requests = [getJourneyTotal(params)];
    setLoading(true);
    Promise.all(requests)
      .then(([journeyTotalStatus]) => {
        setJourneyTotal(journeyTotalStatus);
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        console.log(err);
      });
  };
  /** endregion */
  const handleFilterChanges = () => {
    if (filters.journey && !filters.user) {
      setState({
        textBlockOne: `${t('reports.journeys.totalUsers')}`,
        descriptionBlockOne: `${t(
          'reports.journeys.journey_totalUsers_description',
        )}`,
        textBlockTwo: `${t('reports.journeys.usersCompleted')}`,
        descriptionBlockTwo: `${t(
          'reports.journeys.usersCompleted_description',
        )}`,
        textBlockThree: `${t('reports.journeys.usersProgress')}`,
        descriptionBlockThree: `${t(
          'reports.journeys.usersProgress_description',
        )}`,
      });
    } else if (filters.user && !filters.journey) {
      setState({
        textBlockOne: `${t('reports.journeys.totalJourneys')}`,
        descriptionBlockOne: `${t(
          'reports.journeys.totalJourneys_description',
        )}`,
        textBlockTwo: `${t('reports.journeys.journeysFinished')}`,
        descriptionBlockTwo: `${t(
          'reports.journeys.journeysFinished_description',
        )}`,
        textBlockThree: `${t('reports.journeys.journeysInProgress')}`,
        descriptionBlockThree: `${t(
          'reports.journeys.journeysInProgress_description',
        )}`,
      });
    } else if (filters.user && filters.journey) {
      setState({
        textBlockOne: `${t('reports.journeys.stepsTotal')}`,
        descriptionBlockOne: `${t('reports.journeys.stepsTotal_description')}`,
        textBlockTwo: `${t('reports.journeys.stepsFinished')}`,
        descriptionBlockTwo: `${t(
          'reports.journeys.stepsFinished_description',
        )}`,
        textBlockThree: `${t('reports.journeys.totalScore')}`,
        descriptionBlockThree: `${t(
          'reports.journeys.totalScore_description',
        )}`,
      });
    } else {
      setState({
        textBlockOne: `${t('reports.journeys.total')}`,
        descriptionBlockOne: `${t('reports.journeys.total')}`,
        textBlockTwo: `${t('reports.journeys.totalUsers')}`,
        descriptionBlockTwo: `${t('reports.journeys.totalUsers_description')}`,
        textBlockThree: `${t('reports.journeys.currentActiveUsers')}`,
        descriptionBlockThree: `${t(
          'reports.journeys.currentActiveUsers_description',
        )}`,
      });
    }
  };

  const handleGetResults = () => {
    const params = {};
    if (filters.language) {
      params.language = filters.language;
    }
    if (filters.journey) {
      params.journey = filters.journey;
    }
    if (filters.team) {
      params.team = filters.team;
    }
    if (filters.user) {
      params.user = filters.user;
    }
    if (filters.startedDate && filters.endedDate) {
      params.startedDate = filters.startedDate;
      params.endedDate = filters.endedDate;
    }
    if (filters.user && filters.journey) {
      params.user = filters.user;
      params.journey = filters.journey;
    }
    fetchInfo(params);
  };
  useEffect(() => {
    handleGetResults();
    handleFilterChanges();
  }, [filters]);

  const tableTitle = setSelectedJourney
    ? selectedJourney?.title || t('reports.journeys.table.journeyName')
    : '';
  const tableDescription = setSelectedJourney
    ? `${
        selectedJourney?.createdAt
          ? `(${t('created_at')} ${formatDate(
              selectedJourney.createdAt,
              'DD MMM, YYYY',
            )})`
          : ''
      }`
    : '';

  const getRowParams = () => {
    const params = {};
    if (filters.language) {
      params.language = filters.language;
    }
    if (filters.startedDate) {
      params.startedDate = filters.startedDate;
    }
    if (filters.endedDate) {
      params.endedDate = filters.endedDate;
    }
    if (filters.team) {
      params.team = filters.team;
    }
    return params;
  };

  const getRowDetails = () => row => subTableJourney(row, getRowParams);

  return (
    <Title title={t('myReports')}>
      <ContentContainer>
        <SubHeader title={t('myReports')} />
        <TopicBanner />
        <div className="m-content">
          <div className="full-page-container-with-subheader">
            <div
              id="journeysReportsLayout"
              className="report-journey-content-layout"
            >
              {isJourneyExporting && (
                <div
                  style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(255,255,255,0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999,
                  }}
                >
                  <div id="journeyReportExportLoader" />
                </div>
              )}
              {/** region FILTERS */}
              <div className="row">
                <div className="col-lg-12">
                  <div className="m-portlet m-portlet--mobile">
                    <div className="report-filters-content">
                      <ReportTabBtns tabActive={REPORT_TAB_TYPE.JOURNEY} />
                      <div className="row">
                        <div className="row col-lg-12 report-filters-inputs">
                          {showTeamPicker && (
                            <div className="col-lg-3">
                              <Select
                                id="filterTeam"
                                name={t('reports.scorm.filter.team')}
                                placeholder={t(
                                  'reports.scorm.filter.team.placeholder',
                                )}
                                type="select2"
                                onChange={handleFilterChange('team')}
                                value={filters.team}
                                className="form-control m-input"
                                multiple={false}
                                items={teams.map(te => ({
                                  id: te._id,
                                  text: te.name,
                                }))}
                              />
                            </div>
                          )}
                          <div className="col-lg-3">
                            <AsyncSelector
                              key={filters.team}
                              url={`/table/journey/minified/list?isPublic=true&managerTrackable=true&team=${filters.team}`}
                              params={{}}
                              query={{}}
                              onDetailEndpoint={() =>
                                '/table/journey/minified/list'
                              }
                              displayNameInSelector={item => item.title}
                              id="journeyFilter"
                              headers={{
                                'Accept-Version': '1.0.0',
                                'X-Gamification-API': 'Games',
                              }}
                              onChange={handleFilterChange('journey')}
                              placeholder={t('journey')}
                              groupProps={{
                                ignoreWrapper: true,
                              }}
                              value={filters.journey}
                              allowClear={false}
                              ignoreWrapper
                              pageSize={10}
                              onChangeFullObject
                            />
                          </div>
                          <div className="col-lg-3">
                            <DateRange
                              id="filterDate"
                              placeholder={t('trainers.edit.selectData')}
                              startDate={filters.startedDate}
                              endDate={filters.endedDate}
                              onChange={({ startDate, endDate }) => {
                                handleDateChange(startDate, endDate);
                              }}
                              onClear={() => {
                                handleDateChange(null, null);
                              }}
                              required={false}
                            />
                          </div>
                          <div className="col-lg-3">
                            <button
                              type="button"
                              onClick={handleShowReport}
                              disabled={filters.challenge === null}
                              className="btn btn-dynamic"
                            >
                              {t('reports.scorm.see_results')}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {/** endregion FILTERS */}

              {filters.journey && showReport && (
                <>
                  {/** region TOTALS */}
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="m-portlet m-portlet--mobile">
                        <div className="row">
                          <div className="col-lg-12 row">
                            <div className="col-lg-4 col-md-4 col-sm-12 reports-data">
                              <div className="row reports-data_holder">
                                <div className="col-lg-12 report-total-wrapper">
                                  <h5>{textBlockOne}</h5>
                                  <div
                                    className={
                                      filters.journey && !filters.user
                                        ? 'container-big-content'
                                        : ''
                                    }
                                  >
                                    {descriptionBlockOne}
                                  </div>
                                  <p className="report-number">
                                    {journeyTotal.field1}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="col-lg-4 col-md-4 col-sm-12 reports-data">
                              <div className="row reports-data_holder">
                                <div className="col-lg-12 report-total-wrapper">
                                  <h5>{textBlockTwo}</h5>
                                  <div
                                    className={
                                      filters.journey && !filters.user
                                        ? 'container-big-content'
                                        : ''
                                    }
                                  >
                                    {descriptionBlockTwo}
                                  </div>
                                  <p className="report-number">
                                    {journeyTotal.field2}
                                  </p>
                                </div>
                              </div>
                            </div>

                            <div className="col-lg-4 col-md-4 col-sm-12 reports-data">
                              <div className="row reports-data_holder">
                                <div className="col-lg-12 report-total-wrapper">
                                  <h5>{textBlockThree}</h5>
                                  <div
                                    className={
                                      filters.journey && !filters.user
                                        ? 'container-big-content'
                                        : ''
                                    }
                                  >
                                    {descriptionBlockThree}
                                  </div>
                                  <p className="report-number">
                                    {journeyTotal.field3}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/** endregion TOTALS */}

                  {/** region TABLE */}
                  <div
                    className="row"
                    style={{
                      display: 'block',
                    }}
                  >
                    <div className="col-lg-12">
                      <BasicPortlet
                        title={tableTitle}
                        subtitle={tableDescription}
                        titleClass="m--font-primary"
                        mainTitleClass="upperCase-title"
                        toolClass="btn btn-brand btn-sm"
                        isButton
                        toolAnchor={handleJourneyExport}
                        disabled={isJourneyExporting}
                        toolTitle={t('reports.journeys.table.export_excel')}
                      >
                        <div
                          className={
                            filters.journey &&
                            !filters.user &&
                            'custom-journey-table'
                          }
                        >
                          <List
                            ref={tableEl}
                            id={tableId}
                            key={filters.journey}
                            dataSourceURL={`/table/analytics/journey/${filters?.journey}`}
                            columns={[
                              {
                                field: '_id',
                                title: '',
                                sortable: !1,
                                width: 10,
                                textAlign: 'center',
                                template: step =>
                                  renderToStaticMarkup(<div>{step._id}</div>),
                              },
                              {
                                field: 'row',
                                title: t('reports.journeys.table.step'),
                                sortable: !1,
                                width: 150,
                                textAlign: 'center',
                                template: (row, index) =>
                                  renderToStaticMarkup(index + 1),
                              },
                              {
                                field: 'type',
                                title: t('reports.journeys.table.type'),
                                textAlign: 'center',
                                width: 150,
                                template: ({ type }) =>
                                  t(`reports.journeys.type.${type}`),
                              },
                              {
                                field: 'title',
                                title: t('reports.journeys.table.name'),
                                textAlign: 'center',
                                width: 150,
                                template: step => renderStepName(step),
                              },
                              {
                                field: 'totalActiveUsers',
                                title: t('reports.journeys.finished.users'),
                                textAlign: 'center',
                                width: 100,
                                template: ({
                                  totalFinishedUsers,
                                  totalAffectedUsers,
                                }) =>
                                  renderToStaticMarkup(
                                    <div>
                                      {`${
                                        totalFinishedUsers >= 0
                                          ? totalFinishedUsers
                                          : '-'
                                      }/${
                                        totalAffectedUsers >= 0
                                          ? totalAffectedUsers
                                          : '-'
                                      }`}
                                    </div>,
                                  ),
                              },
                            ]}
                            dataSourceHeaders={{
                              'Accept-Version': '1.0.0',
                              'X-Gamification-API': 'Games',
                            }}
                            query={{
                              language: filters.language,
                              startedDate: filters.startedDate,
                              endedDate: filters.endedDate,
                              team: filters.team,
                            }}
                            detail={{
                              title: t('reports.journeys.details'),
                              content: getRowDetails(
                                filters.journey,
                                filters.user,
                              ),
                            }}
                            sortable={false}
                          />
                        </div>
                      </BasicPortlet>
                    </div>
                  </div>
                  {/** endregion Chart */}

                  <CertificateZipList
                    filters={{
                      ...filters,
                      type: 'journeys',
                    }}
                  />

                  {(selectedJourney?.reportShowDocuments ?? true) && (
                    <SignedDocumentsList
                      filters={{
                        user: filters.user || undefined,
                        journey: filters.journey || undefined,
                        dateStart: filters.startedDate || undefined,
                        dateEnd: filters.endedDate || undefined,
                        showAllDocuments: true,
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </ContentContainer>
    </Title>
  );
}

ReportJourney.propTypes = {
  t: PropTypes.func.isRequired,
  teams: PropTypes.arrayOf(PropTypes.shape(TeamProp.propType)),
};

ReportJourney.defaultProps = {
  teams: [],
};

export default compose(translate)(ReportJourney);
